import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import * as querystring from 'query-string';
import SplashPage from './SplashPage';
import { useFetch, tryFetchFunction } from '../../hooks/useFetch';
import {
    redeemRemoteLockCode,
    setRemoteLockResourceForProperty,
    RemoteLockResourceSelection,
    RemoteLockRedemption
} from '../../api/PropertyIntegration';
import { useState, useEffect } from 'react';
import { browserHistory } from '../../utilities/history';
import FormSelect from '../common/form/FormSelect';
import CenteredLayout from '../layouts/CenteredLayout';
import '../../styles/LockConnect.css';
import Button from '../common/form/Button';

interface QueryResult {
    code: string;
    state: string;
}

interface Props extends RouteComponentProps<{}> { }

export const LockConnectPage = (props: Props) => {
    let query = props.location.search
        ? (querystring.parse(props.location.search) as QueryResult)
        : undefined;
    let code = query && query.code;
    let propertyId = parseInt((query && query.state) || '', 10);

    let codeRedemption: RemoteLockRedemption | undefined =
        code && propertyId ? { redeemCode: code, propertyId: propertyId } : undefined;

    const { data: resources, loading: loading, setData: setResources, error: error } = useFetch(
        tryFetchFunction(redeemRemoteLockCode, codeRedemption, 'RemoteLock connection failed'),
        [],
        undefined
    );

    const [usingGroup, setUsingGroup] = useState<boolean>(true);
    const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);
    const [selectedAlgorithmicLockId, setSelectedAlgorithmicLockId] = useState<string | undefined>(
        undefined
    );
    const [labelText, setLabelText] = useState<string>('');

    const loadingEffect = () => {
        if (loading) {
            setLabelText(`Loading property resources`);
        } else {
            setLabelText(
                error === undefined
                    ? 'No items found. Please add a group or ResortLock and re-authenticate your account.'
                    : `${error}. Please try refreshing.`
            );
        }
    };

    useEffect(loadingEffect, [loading, error]);

    const submitDisabled = usingGroup ? !selectedGroupId : !selectedAlgorithmicLockId;

    const handleSubmit = async () => {
        if (!resources || submitDisabled) {
            return;
        }

        let selectedResource: RemoteLockResourceSelection | undefined = undefined;

        if (usingGroup) {
            let selectedGroup = resources.groups.find(group => group.id === selectedGroupId);
            selectedResource = selectedGroup && {
                groupId: selectedGroupId,
                name: selectedGroup.attributes.name,
                algorithmicLockId: undefined
            };
        } else {
            let selectedAlgorithmicLock = resources.locks.find(
                lock => lock.id === selectedAlgorithmicLockId
            );
            selectedResource = selectedAlgorithmicLock && {
                name: selectedAlgorithmicLock.attributes.name,
                algorithmicLockId: selectedAlgorithmicLockId,
                groupId: undefined
            };
        }

        if (selectedResource === undefined) {
            return;
        }
        let result = await setRemoteLockResourceForProperty(propertyId, selectedResource);
        if (result.success) {
            browserHistory.push('admin/manage');
        } else {
            setResources(undefined);
            setLabelText(result.error || 'Failed to save');
        }
    };

    const groupText = 'a lock group';
    const lockText = 'a ResortLock';

    if (!resources || resources.groups === []) {
        return <SplashPage text={labelText} />;
    } else {
        return (
            <CenteredLayout>
                <div className="col-4">
                    <div>
                        <p>
                            Configuring {usingGroup ? groupText : lockText}. (
                            <button
                                className="underline-button"
                                onClick={() => setUsingGroup(!usingGroup)}
                            >
                                Configure {usingGroup ? lockText : groupText} instead.
                            </button>
                            )
                        </p>
                        <p>
                            Prospects that schedule a tour with this property will be given a code
                            to access {usingGroup ? 'all locks in the group' : 'the lock'} for the
                            duration of their tour.
                        </p>
                    </div>
                    {usingGroup ? (
                        <FormSelect
                            selectProps={{
                                name: 'groupSelect',
                                id: 'group-select',
                                value: selectedGroupId
                            }}
                            selectOptions={resources.groups.map(group => ({
                                label: group.attributes.name,
                                value: group.id
                            }))}
                            onChange={(name, value) => {
                                setSelectedGroupId(value ? value.toString() : undefined);
                            }}
                        />
                    ) : (
                            <FormSelect
                                onChange={(name, value) => {
                                    setSelectedAlgorithmicLockId(value ? value.toString() : undefined);
                                }}
                                selectProps={{
                                    name: 'lockSelect',
                                    id: 'lock-select',
                                    value: selectedAlgorithmicLockId
                                }}
                                selectOptions={resources.locks.map(lock => ({
                                    label: lock.attributes.name,
                                    value: lock.id
                                }))}
                            />
                        )}
                    <br />
                    <Button
                        text="Save"
                        type="submit"
                        onClick={handleSubmit}
                        disabled={submitDisabled}
                    />
                </div>
            </CenteredLayout>
        );
    }
};

export default LockConnectPage;



// WEBPACK FOOTER //
// ./src/components/pages/LockConnectPage.tsx